'use strict';

/**
 * Pulse snapshot totals + factual status line. Cents-safe via Math.round(*100).
 */

function money(v) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function formatUsd(n) {
  return `$${money(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pulseTotals({ original, changeOrders, billed, collected, held, open, unpaidReleases }) {
  const orig = money(original);
  const cos = money(changeOrders);
  const heldAmt = money(held);
  const unpaid = money(unpaidReleases);
  const billedAmt = money(billed);
  const collectedAmt = money(collected);
  const openAmt = open != null ? money(open) : money(Math.max(0, billedAmt - heldAmt - collectedAmt));
  return {
    original: orig,
    changeOrders: cos,
    revised: money(orig + cos),
    billed: billedAmt,
    collected: collectedAmt,
    held: heldAmt,
    open: openAmt,
    unpaidReleases: unpaid,
    retainageDue: money(heldAmt + unpaid),
  };
}

function openPayAppLabel(pa) {
  const n = pa.app_number != null ? pa.app_number : pa.id;
  return `PA ${n}`;
}

function pulseStatus({ totals, openPayApps }) {
  const apps = Array.isArray(openPayApps) ? openPayApps : [];
  const { revised, billed, collected, retainageDue } = totals;
  const openAmt = money(apps.reduce((s, pa) => s + money(pa.amount_due) - money(pa.amount_paid), 0));
  const sources = apps.map(openPayAppLabel).join(', ');
  const billingComplete = revised > 0 && billed >= revised - 0.02;
  const fullyCollected = billingComplete && retainageDue <= 0.02 && collected >= billed - 0.02;

  if (fullyCollected) {
    return {
      state: 'fully_collected',
      line: 'Billing complete · Fully collected',
      sentence: `${formatUsd(collected)} collected. Nothing outstanding.`,
    };
  }
  if (billingComplete && retainageDue > 0.02) {
    return {
      state: 'retainage_open',
      line: 'Billing complete · Retainage outstanding',
      sentence: `${formatUsd(collected)} collected. The remaining ${formatUsd(retainageDue)} is retainage${sources ? ` from ${sources}` : ''}.`,
    };
  }
  const n = apps.length;
  const noun = n === 1 ? 'pay app' : 'pay apps';
  const lineAmt = n === 1 ? ` ${formatUsd(openAmt)}` : '';
  return {
    state: 'active',
    line: `Active · ${n} open ${noun}${lineAmt}`,
    sentence: n
      ? `${formatUsd(openAmt)} open on ${sources}.`
      : 'No open pay apps.',
  };
}

function retainageHeldFromApps(payApps) {
  const apps = (Array.isArray(payApps) ? payApps : []).filter((pa) => !pa.deleted_at);
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const nonRelease = apps
    .filter((pa) => !pa.is_retainage_release && pa.retention_held != null && isPostedPayApp(pa))
    .sort((a, b) => num(b.app_number) - num(a.app_number));
  const accumulated = num(nonRelease[0] && nonRelease[0].retention_held);
  const releaseApps = apps.filter((pa) => pa.is_retainage_release && isPostedPayApp(pa));
  const releasedCreated = releaseApps.reduce((s, pa) => s + num(pa.amount_due), 0);
  const releasedPaid = releaseApps
    .filter((pa) => pa.status === 'paid' || pa.status === 'payment_received' || num(pa.amount_paid) > 0)
    .reduce((s, pa) => s + num(pa.amount_due), 0);
  return {
    held: Math.max(0, Math.round((accumulated - releasedCreated) * 100) / 100),
    releasedUnpaid: Math.max(0, Math.round((releasedCreated - releasedPaid) * 100) / 100),
  };
}

function isPostedPayApp(pa) {
  const st = String(pa?.status || '');
  return st === 'submitted' || st === 'paid' || st === 'payment_received';
}

function postedBilledFromList(payApps) {
  return (payApps || []).reduce((s, pa) => {
    if (pa.deleted_at || !isPostedPayApp(pa)) return s;
    return s + money(pa.amount_due);
  }, 0);
}

/** Recorded receipts count even when legacy payment_status is unset or only partial. */
function paymentsReceivedFromPayApps(payApps) {
  const apps = (Array.isArray(payApps) ? payApps : [])
    .filter((pa) => !pa.deleted_at && money(pa.amount_paid) > 0);
  return { apps, collected: money(apps.reduce((sum, pa) => sum + money(pa.amount_paid), 0)) };
}

function openPayAppsFromList(payApps) {
  return (payApps || []).filter((pa) => {
    if (pa.deleted_at) return false;
    const st = String(pa.status || '');
    if (st === 'draft') return false;
    const due = money(pa.amount_due);
    const paid = money(pa.amount_paid);
    if ((st === 'submitted' || st === 'approved') && due - paid > 0.02) return true;
    return false;
  });
}

export { money, formatUsd, pulseTotals, pulseStatus, openPayAppsFromList, postedBilledFromList, retainageHeldFromApps, isPostedPayApp, paymentsReceivedFromPayApps };
